import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Button } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { X } from "react-feather";
import { getMachineOptions } from '../../../actions/machine';
import { getTransportOptions } from '../../../actions/transport';
import { useHistory, useLocation } from 'react-router-dom';
import useActiveFirm from '../../../hooks/useActiveFirm';

const ExpenseFilters = ({ filters, onFilterChange }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { firmId } = useActiveFirm();
    const { expense_data } = useSelector(state => state.sparepart_expense);
    const { machine_options } = useSelector(state => state.machine);
    const { transport_options } = useSelector(state => state.transport);
    
    const [localFilters, setLocalFilters] = useState({
        spare_part_id: filters.spare_part_id || '',
        warehouse_id: filters.warehouse_id || '',
        consumed_by: filters.consumed_by || '',
        machine_id: filters.machine_id || '',
        transport_id: filters.transport_id || '',
        date_from: filters.date_from || '',
        date_to: filters.date_to || ''
    });

    // Date range state
    const [dateRange, setDateRange] = useState([
        filters.date_from ? moment(filters.date_from).toDate() : null,
        filters.date_to ? moment(filters.date_to).toDate() : null
    ]);

    const [selectedSparePart, setSelectedSparePart] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedConsumedBy, setSelectedConsumedBy] = useState(null);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [selectedTransport, setSelectedTransport] = useState(null);
    const [isUserInteracting, setIsUserInteracting] = useState(false);
    
    // Use refs to track if we've already fetched for this firmId
    const lastFirmIdRef = useRef(null);
    const hasFetchedMachineRef = useRef(false);
    const hasFetchedTransportRef = useRef(false);

    // Load options on component mount and when firmId changes
    useEffect(() => {
        if (!firmId) return; // Don't fetch if no firm is selected
        
        // Reset fetch flags if firmId changed
        if (lastFirmIdRef.current !== firmId) {
            lastFirmIdRef.current = firmId;
            hasFetchedMachineRef.current = false;
            hasFetchedTransportRef.current = false;
        }
        
        // Only fetch if we haven't fetched for this firmId yet
        if (!hasFetchedMachineRef.current && (!machine_options || machine_options.length === 0)) {
            hasFetchedMachineRef.current = true;
            dispatch(getMachineOptions({ firm_id: firmId }));
        }
        if (!hasFetchedTransportRef.current && (!transport_options || transport_options.length === 0)) {
            hasFetchedTransportRef.current = true;
            dispatch(getTransportOptions({ firm_id: firmId }));
        }
    }, [dispatch, firmId]); // Only depend on firmId, not on options to prevent infinite loops

    // Read filters from URL on initial load only (excluding firm_id as it comes from global state)
    useEffect(() => {
        if (!firmId) return; // Don't load filters until firm is selected
        
        const urlParams = new URLSearchParams(location.search);
        const urlFilters = {};
        
        ['warehouse_id', 'spare_part_id', 'consumed_by', 'machine_id', 'transport_id', 'date_from', 'date_to'].forEach(key => {
            const value = urlParams.get(key);
            if (value) {
                urlFilters[key] = value;
            }
        });
        
        // Always include firm_id from global active firm
        urlFilters.firm_id = firmId;
        
        setLocalFilters(prev => {
            // Only update if filters actually changed
            const hasChanges = Object.keys(urlFilters).some(key => 
                key !== 'firm_id' && prev[key] !== urlFilters[key]
            );
            if (!hasChanges && prev.firm_id === firmId) {
                return prev;
            }
            return { ...prev, ...urlFilters };
        });
        
        // Only call onFilterChange if we have filters to apply
        if (Object.keys(urlFilters).length > 0) {
            onFilterChange(urlFilters);
        }
    }, [firmId]); // Only depend on firmId - URL changes handled by individual handlers

    const sparePartOptions = useMemo(() => {
        let spareParts = expense_data.spare_parts || [];
        
        // Filter spare parts by selected warehouse
        if (localFilters.warehouse_id) {
            spareParts = spareParts.filter(part => 
                part.warehouse_id === parseInt(localFilters.warehouse_id)
            );
        }
        // If no warehouse but firm is selected, filter by firm
        else if (firmId) {
            spareParts = spareParts.filter(part => 
                part.warehouse?.firm_id === parseInt(firmId)
            );
        }
        
        return spareParts.map(part => ({
            value: part.id,
            label: `${part.name} - ${part.warehouse?.name || ''}`
        }));
    }, [expense_data.spare_parts, localFilters.warehouse_id, firmId]);

    const warehouseOptions = useMemo(() => {
        let warehouses = expense_data.warehouses || [];
        
        // Filter warehouses by active firm
        if (firmId) {
            warehouses = warehouses.filter(warehouse => 
                warehouse.firm_id === parseInt(firmId)
            );
        }
        
        return warehouses.map(warehouse => ({
            value: warehouse.id,
            label: warehouse.name
        }));
    }, [expense_data.warehouses, firmId]);

    const userOptions = useMemo(() => {
        return expense_data.users?.map(user => ({
            value: user.id,
            label: user.name
        })) || [];
    }, [expense_data.users]);

    const machineOptions = useMemo(() => {
        let machines = expense_data.machines || machine_options || [];
        
        // Filter machines by active firm
        if (firmId) {
            machines = machines.filter(machine => 
                machine.firm_id === parseInt(firmId)
            );
        }
        
        return machines.map(machine => ({
            value: machine.id,
            label: machine.name
        }));
    }, [expense_data.machines, machine_options, firmId]);

    const transportOptions = useMemo(() => {
        let transports = expense_data.transports || transport_options || [];
        
        // Show all transports (not filtered by firm)
        return transports.map(transport => ({
            value: transport.id,
            label: `${transport.name}${transport.plate ? ` - ${transport.plate}` : ''}`
        }));
    }, [expense_data.transports, transport_options]);

    // Initialize selected values when options or filters change
    useEffect(() => {
        // Don't override user selections during interaction
        if (isUserInteracting) {
            setIsUserInteracting(false);
            return;
        }

        if (filters.spare_part_id && sparePartOptions.length > 0) {
            const sparePart = sparePartOptions.find(p => p.value === parseInt(filters.spare_part_id));
            if (sparePart) {
                setSelectedSparePart(sparePart);
            } else {
                // If spare part not found in filtered options (due to warehouse/firm change), clear it
                setSelectedSparePart(null);
            }
        } else {
            setSelectedSparePart(null);
        }
        
        if (filters.warehouse_id && warehouseOptions.length > 0) {
            const warehouse = warehouseOptions.find(w => w.value === parseInt(filters.warehouse_id));
            if (warehouse) {
                setSelectedWarehouse(warehouse);
            } else {
                // If warehouse not found in filtered options (due to firm change), clear it
                setSelectedWarehouse(null);
            }
        } else {
            setSelectedWarehouse(null);
        }
        
        if (filters.consumed_by && userOptions.length > 0) {
            const user = userOptions.find(u => u.value === parseInt(filters.consumed_by));
            if (user) {
                setSelectedConsumedBy(user);
            }
        } else {
            setSelectedConsumedBy(null);
        }

        if (filters.machine_id && machineOptions.length > 0) {
            const machine = machineOptions.find(m => m.value === parseInt(filters.machine_id));
            if (machine) {
                setSelectedMachine(machine);
            } else {
                setSelectedMachine(null);
            }
        } else {
            setSelectedMachine(null);
        }

        if (filters.transport_id && transportOptions.length > 0) {
            const transport = transportOptions.find(t => t.value === parseInt(filters.transport_id));
            if (transport) {
                setSelectedTransport(transport);
            } else {
                setSelectedTransport(null);
            }
        } else {
            setSelectedTransport(null);
        }
    }, [sparePartOptions, warehouseOptions, userOptions, filters.spare_part_id, filters.warehouse_id, filters.consumed_by, machineOptions, transportOptions, filters.machine_id, filters.transport_id]);

    // Update date range when filters change from parent
    useEffect(() => {
        setDateRange([
            filters.date_from ? moment(filters.date_from).toDate() : null,
            filters.date_to ? moment(filters.date_to).toDate() : null
        ]);
    }, [filters.date_from, filters.date_to]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setLocalFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateRangeChange = (update) => {
        setDateRange(update);
        const [startDate, endDate] = update;
        
        const newFilters = {
            ...localFilters,
            date_from: startDate ? moment(startDate).format('YYYY-MM-DD') : '',
            date_to: endDate ? moment(endDate).format('YYYY-MM-DD') : ''
        };
        
        // Always include firm_id from global active firm
        if (firmId) {
            newFilters.firm_id = firmId;
        }
        
        setLocalFilters(newFilters);
        updateURLWithFilters(newFilters);
        onFilterChange(newFilters);
    };

    const handleDateRangeReset = () => {
        setDateRange([null, null]);
        const newFilters = {
            ...localFilters,
            date_from: '',
            date_to: ''
        };
        
        // Always include firm_id from global active firm
        if (firmId) {
            newFilters.firm_id = firmId;
        }
        
        setLocalFilters(newFilters);
        updateURLWithFilters(newFilters);
        onFilterChange(newFilters);
    };

    // Helper function to update URL with filters
    const updateURLWithFilters = (filters) => {
        const urlParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            // Don't include firm_id in URL as it comes from global state
            if (key !== 'firm_id' && value && value !== '') {
                urlParams.set(key, value);
            }
        });
        
        const newUrl = `${location.pathname}?${urlParams.toString()}`;
        history.push(newUrl);
    };

    const handleSelectChange = (name, selectedOption) => {
        const value = selectedOption?.value || '';
        setIsUserInteracting(true);

        // Update selected state and filters based on the field
        switch (name) {
            case 'spare_part_id':
                setSelectedSparePart(selectedOption);
                const sparePartFilters = {
                    ...localFilters,
                    spare_part_id: value
                };
                // Always include firm_id from global active firm
                if (firmId) {
                    sparePartFilters.firm_id = firmId;
                }
                setLocalFilters(sparePartFilters);
                updateURLWithFilters(sparePartFilters);
                onFilterChange(sparePartFilters);
                break;
            case 'warehouse_id':
                setSelectedWarehouse(selectedOption);
                // Clear spare part selection when warehouse changes
                setSelectedSparePart(null);
                const warehouseFilters = {
                    ...localFilters,
                    warehouse_id: value,
                    spare_part_id: ''
                };
                // Always include firm_id from global active firm
                if (firmId) {
                    warehouseFilters.firm_id = firmId;
                }
                setLocalFilters(warehouseFilters);
                updateURLWithFilters(warehouseFilters);
                onFilterChange(warehouseFilters);
                break;
            case 'consumed_by':
                setSelectedConsumedBy(selectedOption);
                const userFilters = {
                    ...localFilters,
                    consumed_by: value
                };
                // Always include firm_id from global active firm
                if (firmId) {
                    userFilters.firm_id = firmId;
                }
                setLocalFilters(userFilters);
                updateURLWithFilters(userFilters);
                onFilterChange(userFilters);
                break;
            case 'machine_id':
                setSelectedMachine(selectedOption);
                const machineFilters = {
                    ...localFilters,
                    machine_id: value
                };
                // Always include firm_id from global active firm
                if (firmId) {
                    machineFilters.firm_id = firmId;
                }
                setLocalFilters(machineFilters);
                updateURLWithFilters(machineFilters);
                onFilterChange(machineFilters);
                break;
            case 'transport_id':
                setSelectedTransport(selectedOption);
                const transportFilters = {
                    ...localFilters,
                    transport_id: value
                };
                // Always include firm_id from global active firm
                if (firmId) {
                    transportFilters.firm_id = firmId;
                }
                setLocalFilters(transportFilters);
                updateURLWithFilters(transportFilters);
                onFilterChange(transportFilters);
                break;
        }
    };



    const handleClearFilters = () => {
        const emptyFilters = {
            spare_part_id: '',
            warehouse_id: '',
            consumed_by: '',
            machine_id: '',
            transport_id: '',
            date_from: '',
            date_to: ''
        };
        
        // Always include firm_id from global active firm
        if (firmId) {
            emptyFilters.firm_id = firmId;
        }
        
        setLocalFilters(emptyFilters);
        setDateRange([null, null]);
        setSelectedSparePart(null);
        setSelectedWarehouse(null);
        setSelectedConsumedBy(null);
        setSelectedMachine(null);
        setSelectedTransport(null);
        
        // Clear URL parameters using the helper function
        updateURLWithFilters(emptyFilters);
        
        // Pass the empty filters structure to parent
        onFilterChange(emptyFilters);
    };

    return (
        <div>
            <Row>
                {/* Column 1: Location/Organization */}
                <Col md={4}>
                    <Row>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedWarehouse}
                                onChange={(option) => handleSelectChange('warehouse_id', option)}
                                options={warehouseOptions}
                                placeholder={firmId ? "Omborni tanlang..." : "Firma tanlang"}
                                isClearable
                                isDisabled={!firmId}
                                className="form-select-sm"
                            />
                        </Col>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedSparePart}
                                onChange={(option) => handleSelectChange('spare_part_id', option)}
                                options={sparePartOptions}
                                placeholder={!firmId && !localFilters.warehouse_id ? "Avval omborni tanlang" : "Ehtiyot qismni tanlang..."}
                                isClearable
                                isSearchable
                                isDisabled={!firmId && !localFilters.warehouse_id}
                                className="form-select-sm"
                            />
                        </Col>
                    </Row>
                </Col>

                {/* Column 2: Resources/Assets */}
                <Col md={4}>
                    <Row>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedMachine}
                                onChange={(option) => handleSelectChange('machine_id', option)}
                                options={machineOptions}
                                placeholder={!firmId ? "Firma tanlang" : "Uskunani tanlang..."}
                                isClearable
                                isSearchable
                                isDisabled={!firmId}
                                className="form-select-sm"
                            />
                        </Col>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedTransport}
                                onChange={(option) => handleSelectChange('transport_id', option)}
                                options={transportOptions}
                                placeholder="Transportni tanlang..."
                                isClearable
                                isSearchable
                                className="form-select-sm"
                            />
                        </Col>
                        <Col md={12} className="mb-2">
                            <Select
                                value={selectedConsumedBy}
                                onChange={(option) => handleSelectChange('consumed_by', option)}
                                options={userOptions}
                                placeholder="Sarflagan shaxsni tanlang..."
                                isClearable
                                isSearchable
                                className="form-select-sm"
                            />
                        </Col>
                    </Row>
                </Col>

                {/* Column 3: Time/Actions */}
                <Col md={4}>
                    <Row>
                        <Col md={12} className="mb-2">
                            <div className="input-group input-group-sm">
                                <DatePicker
                                    wrapperClassName={'form-control form-control-sm'}
                                    className={'border-0 pe-0 w-100 focus-outline-0'}
                                    placeholderText={"Sana oralig'i: dd/mm/yyyy - dd/mm/yyyy"}
                                    selectsRange={true}
                                    startDate={dateRange[0]}
                                    endDate={dateRange[1]}
                                    onChange={handleDateRangeChange}
                                    dateFormat="dd/MM/yyyy"
                                    isClearable={false}
                                />
                                <span className="input-group-text cursor-pointer" onClick={handleDateRangeReset}>
                                    <X size={14} />
                                </span>
                            </div>
                        </Col>
                        <Col md={12} className="mb-2">
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={handleClearFilters}
                                className="w-100"
                            >
                                Barcha filtrlarni tozalash
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

ExpenseFilters.propTypes = {
    filters: PropTypes.object.isRequired,
    onFilterChange: PropTypes.func.isRequired,
};

export default ExpenseFilters; 